/**
 * FormRegistry — Context provider for route-driven form rendering.
 *
 * Each frontend app populates this context with the generated route metadata
 * and Zod schemas. The FormRenderer reads from it at runtime.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { ZodObject, ZodRawShape } from 'zod';

export interface FormFieldDefinition {
  name: string;
  type: 'string' | 'integer' | 'number' | 'boolean' | 'uuid' | 'object' | 'record' | 'string[]' | 'uuid[]';
  required?: boolean;
  enum?: string[];
  default?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  format?: 'url' | 'email' | 'datetime';
  nullable?: boolean;
  description?: string;
}

export interface FormRouteEntry {
  operationId: string;
  method: string;
  path: string;
  group: string;
  auth: string;
  statusCode: number;
  fields: FormFieldDefinition[];
  schema: ZodObject<ZodRawShape>;
  title?: string;
  description?: string;
}

interface FormRegistryContextValue {
  routes: Map<string, FormRouteEntry>;
}

const FormRegistryContext = createContext<FormRegistryContextValue | null>(null);

export function useFormRegistry(): FormRegistryContextValue {
  const ctx = useContext(FormRegistryContext);
  if (!ctx) {
    throw new Error('useFormRegistry must be used within a <FormRegistryProvider>');
  }
  return ctx;
}

interface FormRegistryProviderProps {
  routes: FormRouteEntry[];
  children: ReactNode;
}

export function FormRegistryProvider({ routes, children }: FormRegistryProviderProps) {
  const map = new Map<string, FormRouteEntry>();
  for (const route of routes) {
    map.set(route.operationId, route);
  }

  return (
    <FormRegistryContext.Provider value={{ routes: map }}>
      {children}
    </FormRegistryContext.Provider>
  );
}
